import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType, concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';

import {
    closeTicket,
    closeTicketFailure,
    closeTicketSuccess,
    loadCandidateContracts,
    loadCandidateContractsFailure,
    loadCandidateContractsSuccess,
    loadCandidates,
    loadCandidatesFailure,
    loadCandidatesSuccess,
    loadContract,
    loadContractSuccess,
    loadContractFailure,
    loadContractDetails,
    loadContractDetailsFailure,
    loadContractDetailsSuccess,
    loadContractLookup,
    loadContractLookupFailure,
    loadContractLookupSuccess,
    loadStates,
    loadStatesFailure,
    loadStatesSuccess,
    loadTicketTypes,
    loadTicketTypesFailure,
    loadTicketTypesSuccess,
    loadTicketCategories,
    loadTicketCategoriesFailure,
    loadTicketCategoriesSuccess,
    loadTicketCounsels,
    loadTicketCounselsFailure,
    loadTicketCounselsSuccess,
    loadWorkersCompCoordinators,
    loadWorkersCompCoordinatorsFailure,
    loadWorkersCompCoordinatorsSuccess,
    loadTicketDetails,
    loadTicketDetailsFailure,
    loadTicketDetailsSuccess,
    loadUrgency,
    loadUrgencyFailure,
    loadUrgencySuccess,
    reopenTicket,
    reopenTicketFailure,
    reopenTicketSuccess,
    ticketCreate,
    ticketCreateFailure,
    ticketCreateSuccess,
    ticketCreateSuccessNewTab,
    updateTicket,
    updateTicketBookedDays,
    updateTicketBookedDaysSuccess,
    updateTicketCounsel,
    updateTicketCounselFailure,
    updateTicketCounselSuccess,
    updateTicketDescription,
    updateTicketDescriptionFailure,
    updateTicketDescriptionSuccess,
    updateTicketFailure,
    updateTicketSuccess,
    reassignTicket,
    reassignTicketFailure,
    reassignTicketSuccess,
    toggleTicketLockStatus,
    toggleTicketLockStatusFailure,
    toggleTicketLockStatusSuccess,
    updateTicketBookedDaysFailure,
    createStaffingAdvisoryFromTicket,
    createStaffingAdvisoryFromTicketSuccess,
    createStaffingAdvisoryFromTicketFailure,
    updateWorkersCompCoordinator,
    updateWorkersCompCoordinatorSuccess,
    updateWorkersCompCoordinatorFailure
} from 'src/app/root-store/ticket-store/actions/tickets.actions';
import { TicketingState } from 'src/app/root-store/ticket-store/state/ticketing.state';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { TicketService } from 'src/app/travelx/tickets/ticket.service';
import { selectContractLookup, selectStates, selectUrgency } from 'src/app/root-store/ticket-store/tickets.selectors';
import { Router } from '@angular/router';
import { WINDOW } from 'src/app/core/core.module';
import { HttpResponseHelpers } from 'src/app/shared/helpers';
import { HttpResponseBase } from '@angular/common/http';

@Injectable()
export class TicketDetailsEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _router: Router,
        private readonly _ticketService: TicketService,
        private readonly _store: Store<TicketingState>,
        @Inject(WINDOW) private readonly _window: Window
    ) {}

    getTicketById$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                loadTicketDetails,
                closeTicketSuccess,
                reopenTicketSuccess,
                updateTicketSuccess,
                reassignTicketSuccess,
                toggleTicketLockStatusSuccess,
                createStaffingAdvisoryFromTicketSuccess
            ),
            switchMap((action) => {
                return this._ticketService.getTicketDetails(action.ticketId).pipe(
                    map((result) => loadTicketDetailsSuccess({ ticketDetails: result })),
                    catchError((error: unknown) =>
                        of(
                            loadTicketDetailsFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error),
                                isForbidden: (error as HttpResponseBase)?.status === 403
                            })
                        )
                    )
                );
            })
        );
    });

    updateTicketDescription$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateTicketDescription),
            switchMap((action) => {
                return this._ticketService.updateTicketDescription(action.ticketId, action.note).pipe(
                    map(() =>
                        updateTicketDescriptionSuccess({
                            updatedNote: action.note,
                            message: 'Updated ticket successfully'
                        })
                    ),
                    catchError((error: unknown) =>
                        of(updateTicketDescriptionFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        );
    });

    updateTicketBookedDays$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateTicketBookedDays),
            switchMap((action) => {
                return this._ticketService.updateTicketBookedDays(action.ticketId, action.description).pipe(
                    map(() => updateTicketBookedDaysSuccess({ updatedDescription: action.description })),
                    catchError((error: unknown) =>
                        of(updateTicketBookedDaysFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        );
    });

    getTicketCounsels$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadTicketCounsels),
            switchMap(() => {
                return this._ticketService.getCounsels().pipe(
                    map((result) => loadTicketCounselsSuccess({ counsels: result })),
                    catchError((error: unknown) => of(loadTicketCounselsFailure({ error })))
                );
            })
        );
    });

    getWorkersCompCoordinators$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadWorkersCompCoordinators),
            switchMap(() => {
                return this._ticketService.getWorkersCompCoordinators().pipe(
                    map((result) => loadWorkersCompCoordinatorsSuccess({ workersCompCoordinators: result })),
                    catchError((error: unknown) => of(loadWorkersCompCoordinatorsFailure({ error })))
                );
            })
        );
    });

    getTicketTypes$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadTicketTypes),
            switchMap(() => {
                return this._ticketService.getTicketTypes().pipe(
                    map((result) => loadTicketTypesSuccess({ ticketTypes: result })),
                    catchError((error: unknown) => of(loadTicketTypesFailure({ error })))
                );
            })
        );
    });

    getTicketCategories$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadTicketCategories),
            switchMap(() => {
                return this._ticketService.getTicketCategories().pipe(
                    map((result) => loadTicketCategoriesSuccess({ categories: result })),
                    catchError((error: unknown) => of(loadTicketCategoriesFailure({ error })))
                );
            })
        );
    });

    getTicketStates$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadStates),
            concatLatestFrom(() => this._store.select(selectStates)),
            concatMap(([action, states]) => {
                if (!states.length) {
                    return this._ticketService.getCountryStates().pipe(
                        map((result) => loadStatesSuccess({ states: result })),
                        catchError((error: unknown) => of(loadStatesFailure({ error })))
                    );
                } else {
                    return of(loadStatesSuccess({ states }));
                }
            })
        );
    });

    getTicketUrgency$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadUrgency),
            concatLatestFrom(() => this._store.select(selectUrgency)),
            concatMap(([action, urgency]) => {
                if (!urgency.length) {
                    return this._ticketService.getTicketUrgency().pipe(
                        map((result) => loadUrgencySuccess({ urgency: result })),
                        catchError((error: unknown) => of(loadUrgencyFailure({ error })))
                    );
                } else {
                    return of(loadUrgencySuccess({ urgency }));
                }
            })
        );
    });

    getTicketContract$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadContract),
            switchMap((action) => {
                return this._ticketService.getContractById(action.ticketId).pipe(
                    map((result) => loadContractSuccess({ contract: result })),
                    catchError((error: unknown) => of(loadContractFailure({ error })))
                );
            })
        );
    });

    getContractDetails$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadContractDetails),
            switchMap((action) => {
                return this._ticketService.getContractDetails(action.contractId).pipe(
                    map((result) => loadContractDetailsSuccess({ travelContract: result })),
                    catchError((error: unknown) => of(loadContractDetailsFailure({ error })))
                );
            })
        );
    });

    getTicketContractLookup$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadContractLookup),
            concatLatestFrom(() => this._store.select(selectContractLookup)),
            concatMap(([action, contractLookup]) => {
                if (!contractLookup) {
                    return this._ticketService.getLookups().pipe(
                        map((result) => loadContractLookupSuccess({ contractLookup: result })),
                        catchError((error: unknown) => of(loadContractLookupFailure({ error })))
                    );
                } else {
                    return of(loadContractLookupSuccess({ contractLookup }));
                }
            })
        )
    );

    getTicketCandidateContracts$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadCandidateContracts),
            concatMap((action) => {
                return this._ticketService.getCandidateContracts(action.candidateId).pipe(
                    map((result) => loadCandidateContractsSuccess({ candidateContracts: result })),
                    catchError((error: unknown) => of(loadCandidateContractsFailure({ error })))
                );
            })
        );
    });

    updateTicketCounsel$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateTicketCounsel),
            switchMap((action) => {
                return this._ticketService.updateTicketCounsel(action.ticketId, action.counselId).pipe(
                    map(() =>
                        updateTicketCounselSuccess({
                            counselId: action.counselId,
                            message: 'Updated ticket successfully'
                        })
                    ),
                    catchError((error: unknown) =>
                        of(updateTicketCounselFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        );
    });

    updateWorkersCompCoordinator$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateWorkersCompCoordinator),
            switchMap((action) => {
                return this._ticketService
                    .updateWorkersCompCoordinator(action.ticketId, action.workersCompCoordinatorId)
                    .pipe(
                        map(() =>
                            updateWorkersCompCoordinatorSuccess({
                                workersCompCoordinatorId: action.workersCompCoordinatorId,
                                message: 'Updated workers compensation coordinator successfully'
                            })
                        ),
                        catchError((error: unknown) =>
                            of(
                                updateWorkersCompCoordinatorFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    );
            })
        );
    });

    loadTicketCandidates$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadCandidates),
            switchMap((action) => {
                return this._ticketService.getCandidates(action.query, action.subContract).pipe(
                    map((candidates) => loadCandidatesSuccess({ candidates })),
                    catchError((error: unknown) => of(loadCandidatesFailure({ error })))
                );
            })
        );
    });

    createTicket$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(ticketCreate),
            switchMap((action) => {
                if (action.openNewTab === true) {
                    return this._ticketService.ticketCreate(action.ticket).pipe(
                        map((tickets) => ticketCreateSuccessNewTab({ tickets })),
                        catchError((error: unknown) => of(ticketCreateFailure({ error })))
                    );
                } else {
                    return this._ticketService.ticketCreate(action.ticket).pipe(
                        map((tickets) => ticketCreateSuccess({ tickets })),
                        catchError((error: unknown) => of(ticketCreateFailure({ error })))
                    );
                }
            })
        );
    });

    closeTicket$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(closeTicket),
            switchMap((action) => {
                return this._ticketService.closeTicket(action.ticketId, action.closeParams).pipe(
                    map(() => closeTicketSuccess({ ticketId: action.ticketId })),
                    catchError((error: unknown) => of(closeTicketFailure({ error })))
                );
            })
        );
    });

    reopenTicket$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(reopenTicket),
            switchMap((action) => {
                return this._ticketService.reopenTicket(action.ticketId).pipe(
                    map(() => reopenTicketSuccess({ ticketId: action.ticketId })),
                    catchError((error: unknown) => of(reopenTicketFailure({ error })))
                );
            })
        );
    });

    updateTicket$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateTicket),
            switchMap((action) => {
                return this._ticketService.updateTicket(action.ticketId, action.updateData).pipe(
                    map(() =>
                        updateTicketSuccess({ ticketId: action.ticketId, message: 'Updated ticket successfully' })
                    ),
                    catchError((error: unknown) =>
                        of(updateTicketFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        );
    });

    reassignTicket$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(reassignTicket),
            switchMap((action) => {
                return this._ticketService.reassignTickets(action.assigneeId, [action.ticketId]).pipe(
                    map(() => reassignTicketSuccess({ ticketId: action.ticketId })),
                    catchError((error: unknown) => of(reassignTicketFailure({ error })))
                );
            })
        );
    });

    ticketDetailSuccessToaster$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(updateTicketCounselSuccess, updateTicketDescriptionSuccess, updateTicketSuccess),
                map((action) => this._store.dispatch(showToasterMessage({ message: action.message })))
            );
        },
        { dispatch: false }
    );

    ticketDetailFailureToaster$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(
                    updateTicketDescriptionFailure,
                    updateTicketFailure,
                    updateTicketBookedDaysFailure,
                    updateTicketCounselFailure,
                    toggleTicketLockStatusFailure
                ),
                map((action) => this._store.dispatch(showToasterMessage({ message: action.error as string })))
            );
        },
        { dispatch: false }
    );

    closeSuccessToaster$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(closeTicketSuccess),
                map(() => this._store.dispatch(showToasterMessage({ message: 'Ticket resolved success' })))
            );
        },
        { dispatch: false }
    );

    closeFailureToaster$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(closeTicketFailure),
                map(() => this._store.dispatch(showToasterMessage({ message: 'Ticket resolved failure' })))
            );
        },
        { dispatch: false }
    );

    createFailureToaster$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(ticketCreateFailure),
                map(() => this._store.dispatch(showToasterMessage({ message: 'Ticket create failure' })))
            );
        },
        { dispatch: false }
    );

    createTicketSuccess$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(ticketCreateSuccess),
                map((action) => {
                    this._router.navigate([`/travelx/tickets/${action.tickets[0]}`]);
                    this._store.dispatch(showToasterMessage({ message: 'Ticket created success' }));
                })
            );
        },
        { dispatch: false }
    );

    createTicketSuccessNewTab$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(ticketCreateSuccessNewTab),
                map((action) => {
                    const url = this._router.serializeUrl(
                        this._router.createUrlTree([`/travelx/tickets/${action.tickets[0]}`])
                    );
                    this._window.open(`#${url}`, '_blank');
                    this._store.dispatch(showToasterMessage({ message: 'Ticket created success' }));
                })
            );
        },
        { dispatch: false }
    );

    reopenSuccessToaster$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(reopenTicketSuccess),
                map(() => this._store.dispatch(showToasterMessage({ message: 'Ticket reopen success' })))
            );
        },
        { dispatch: false }
    );

    reopenFailureToaster$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(reopenTicketFailure),
                map(() => this._store.dispatch(showToasterMessage({ message: 'Ticket reopen failure' })))
            );
        },
        { dispatch: false }
    );

    reassignSuccessToaster$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(reassignTicketSuccess),
                map(() => this._store.dispatch(showToasterMessage({ message: 'Ticket reassigned successfully' })))
            );
        },
        { dispatch: false }
    );

    reassignFailureToaster$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(reassignTicketFailure),
                map(() => this._store.dispatch(showToasterMessage({ message: 'Ticket reassign failure' })))
            );
        },
        { dispatch: false }
    );

    successToaster$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(toggleTicketLockStatusSuccess, createStaffingAdvisoryFromTicketSuccess),
                map((action) => this._store.dispatch(showToasterMessage({ message: action.message })))
            );
        },
        { dispatch: false }
    );

    failureToaster$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(toggleTicketLockStatusFailure, createStaffingAdvisoryFromTicketFailure),
                map((action) => this._store.dispatch(showToasterMessage({ message: action.error as string })))
            );
        },
        { dispatch: false }
    );

    toggleTicketLockStatus$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(toggleTicketLockStatus),
            switchMap((action) => {
                return this._ticketService.toggleLockStatus(action.ticketId).pipe(
                    map(() =>
                        toggleTicketLockStatusSuccess({
                            ticketId: action.ticketId,
                            message: 'Ticket lock status updated successfully'
                        })
                    ),
                    catchError((error: unknown) =>
                        of(toggleTicketLockStatusFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        );
    });

    createStaffingAdvisoryFromTicket$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(createStaffingAdvisoryFromTicket),
            switchMap((action) => {
                return this._ticketService.createStaffingAdvisoryFromTicket(action.request).pipe(
                    map((staffingAdvisoryId) =>
                        createStaffingAdvisoryFromTicketSuccess({
                            id: staffingAdvisoryId,
                            message: 'Staffing advisory created successfully',
                            ticketId: action.request.ticketId
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            createStaffingAdvisoryFromTicketFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });
}
